import * as React from 'react';

import {
  App,
  AppInstallMeta,
  Beacon,
  CtaDisplayType,
  GlanceContextType,
  Experiment,
  GlanceDraft,
  GlanceMeta,
  GlanceType,
  PropositionType,
  Role,
  StudioAppConfig,
  BucketPriority,
  Region,
} from 'glance-datatypes';
import { BeaconType, RegionPartnerTuple, CityStates } from '../types';

import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import GlanceDraftCard from './GlanceDraftCard';
import GlanceDraftPublish from './GlanceDraftPublish';
import RestService from '../../../RestService';
import Snackbar from 'material-ui/Snackbar';
import { connect } from 'react-redux';
import Utils from '../../../Utils';

interface BubbleData {
  bubbleId: string;
  name: string;
  qualifier: string;
  imagePath: string;
  storyCount: number;
  glanceType: string;
  adCoefficient: number;
  bucketPriority: BucketPriority;
  ecpm: string;
}

interface Props {
  glanceDraftData: GlanceDraft;
  studioAppConfig?: StudioAppConfig;
  experiments: Map<string, Experiment>;
  errorMsg: string;
  regionPartnerList: RegionPartnerTuple[];
  draftMeta: GlanceMeta[];
  singleClickPublish: (draft: GlanceDraft) => any;
  role: Role;
  copyDraft: (draft: GlanceDraft) => any;
  bubbleData: BubbleData;
  cardType: number;
  disableCopy: boolean;
  cityStates: CityStates[];
}

interface State {
  pristine: boolean;
  draft: GlanceDraft;
  dropImage?: DropImage;
  overlayImage?: DropImage;
  bingeImage?: DropImage;
  removed?: boolean;
  openRemoveConfirmDialog: boolean;
  openSyncConfirmDialog: boolean;
  onPublishClicked: boolean;
  openPublishDialog: boolean;
  publishToPartners?: string[];
  openSnackbar: boolean;
  snackbarMessage: string;
  bubbleData: BubbleData;
  editAdCoefficient: boolean;
  editBucketPriority: boolean;
  editEcpm: boolean;
  editRank: boolean;
  editFCap: boolean;
  displayRank: boolean;
  storyCount: number;
  peekSourceList: string[];
  dspPeekSourceList: string[];
}

interface DropImage {
  image: any;
  attrSource?: string;
  attrCaption?: string;
  attrAuthor?: string;
}

class GlanceDraftComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      pristine: true,
      draft: this.props.glanceDraftData,
      removed: false,
      openRemoveConfirmDialog: false,
      openSyncConfirmDialog: false,
      onPublishClicked: false,
      openPublishDialog: false,
      publishToPartners: [],
      openSnackbar: false,
      snackbarMessage: 'Progress Updates will come here',
      bubbleData: this.props.bubbleData,
      editAdCoefficient: false,
      editBucketPriority: false,
      editEcpm: false,
      editRank: false,
      editFCap: false,
      displayRank: false,
      storyCount: 0,
      peekSourceList: [],
      dspPeekSourceList: [],
    };

    this.onSave = this.onSave.bind(this);
    this.saveDraft = this.saveDraft.bind(this);
    this.onEdit = this.onEdit.bind(this);
    this.onRemove = this.onRemove.bind(this);
    this.onOverride = this.onOverride.bind(this);
    this.onRemoveConfirm = this.onRemoveConfirm.bind(this);
    this.closeConfirmDialog = this.closeConfirmDialog.bind(this);
    this.onPublish = this.onPublish.bind(this);
    this.onImageDrop = this.onImageDrop.bind(this);
    this.onBingeImageDrop = this.onBingeImageDrop.bind(this);
    this.onOverlayImageDrop = this.onOverlayImageDrop.bind(this);
    this.removeImage = this.removeImage.bind(this);
    this.removeBingeImage = this.removeBingeImage.bind(this);
    this.removeOverlayImage = this.removeOverlayImage.bind(this);
    this.onPublishDone = this.onPublishDone.bind(this);
    this.closePublishDialog = this.closePublishDialog.bind(this);
    this.closeSnackbar = this.closeSnackbar.bind(this);
    this.onPause = this.onPause.bind(this);
    this.getDSPAdvertiserGuid = this.getDSPAdvertiserGuid.bind(this);
    this.getAppInstallMeta = this.getAppInstallMeta.bind(this);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (
      JSON.stringify(nextProps.bubbleData) !==
      JSON.stringify(this.props.bubbleData)
    ) {
      this.setState({
        ...this.state,
        bubbleData: nextProps.bubbleData,
      });
    }
  }

  componentDidMount() {
    let adCoefficientDisabled = this.state.draft.adCoefficient ? true : false;
    let bucketPriorityDisabled = this.state.draft.bucketPriority ? true : false;
    let eCpmDisabled = false;
    if (this.state.draft.campaignMeta) {
      eCpmDisabled = this.state.draft.campaignMeta.eCpm ? true : false;
    }
    let sourceNameList: string[] = [];
    let dspSourceNameList: string[] = [];
    this.props.studioAppConfig &&
      this.props.studioAppConfig.apps &&
      this.props.studioAppConfig.apps.forEach((app) => {
        if (app.sourceName && app.sourceName.length > 0) {
          sourceNameList = sourceNameList.length
            ? [...sourceNameList, ...app.sourceName]
            : [...app.sourceName];
          if (app.dspAdvertiserGuid) {
            dspSourceNameList = dspSourceNameList.length
              ? [...dspSourceNameList, ...app.sourceName]
              : [...app.sourceName];
          }
        }
      });
    sourceNameList.sort((app1: any, app2: any) =>
      app1.toLowerCase().localeCompare(app2.toLowerCase()),
    );
    dspSourceNameList.sort((app1: any, app2: any) =>
      app1.toLowerCase().localeCompare(app2.toLowerCase()),
    );
    this.setState({
      ...this.state,
      editAdCoefficient: adCoefficientDisabled,
      editBucketPriority: bucketPriorityDisabled,
      editEcpm: eCpmDisabled,
      peekSourceList: sourceNameList,
      dspPeekSourceList: dspSourceNameList,
    });
  }

  getDSPAdvertiserGuid(sourceName: string) {
    if (sourceName && this.props.studioAppConfig) {
      let dspAdvertiserGuid = '';
      this.props.studioAppConfig.apps.forEach((app) => {
        if (app.sourceName && app.sourceName.length > 0) {
          if (app.sourceName.includes(sourceName) && app.dspAdvertiserGuid) {
            dspAdvertiserGuid = app.dspAdvertiserGuid;
          }
        }
      });
      return dspAdvertiserGuid;
    } else {
      return '';
    }
  }

  getAppInstallMeta(sourceName: string) {
    if (sourceName && this.props.studioAppConfig) {
      let appInstallMeta;
      this.props.studioAppConfig.apps.forEach((app) => {
        if (app.sourceName && app.sourceName.length > 0) {
          if (app.sourceName.includes(sourceName)) {
            appInstallMeta = {
              ...app,
              appName: app.name,
              packageName: app.packageId,
              appBundleUrl: '',
              notifTemplateId: '',
              d1NotifTemplateId: '',
              ltvNotifTemplateId: '',
              preloadEnable: app.preloadEnable ? app.preloadEnable : false,
              contentProviderUrl: app.contentProviderUrl
                ? app.contentProviderUrl
                : '',
            };
          }
        }
      });
      return appInstallMeta;
    }
  }

  onEdit(key: string, value: any) {
    const currentDraft: GlanceDraft = this.state.draft;

    let draftChange: GlanceDraft = this.state.draft;
    let adCoeffDisabled = this.state.editAdCoefficient;
    let bucketPriorityDisabled = this.state.editBucketPriority;
    let eCpmDisabled = this.state.editEcpm;
    let displayRank = this.state.displayRank;
    let storyCount = this.state.storyCount;

    const draftTargeting = currentDraft.targeting || {
      apps: {
        absent: [],
        present: [],
        anyPresent: [],
        anyAbsent: [],
      },
      requiresGPayReadiness: false,
      enableDSPRetargeting: false,
      childLockFlag: null,
    };
    let beacons: Beacon[];
    value = value == null || value.length == 0 ? null : value;
    const getUpdatedEus = (value: { euId: string; region: Region }) => {
      let updatedEus =
        currentDraft.eus !== null
          ? currentDraft.eus.filter((eu) => eu.region !== value.region)
          : [];
      const valueProps =
        currentDraft.valueProps !== null
          ? currentDraft.valueProps.filter(
              (valueProp) => valueProp.region !== value.region,
            )
          : [];
      if (value.euId === null) {
        return {
          updatedEus,
          valueProps,
        };
      }
      updatedEus = [...updatedEus, value];
      return {
        updatedEus,
        valueProps,
      };
    };
    const getUpdatedValueProps = (value: { vpId: string; region: Region }) => {
      let updatedValueProps =
        currentDraft.valueProps !== null
          ? currentDraft.valueProps.filter((vp) => vp.region !== value.region)
          : [];
      if (value.vpId !== null) {
        updatedValueProps = [...updatedValueProps, value];
      }
      return {
        updatedValueProps,
      };
    };

    switch (key) {
      case 'rankInBubble':
        draftChange = {
          ...currentDraft,
          rankInBubble: value,
          ...(value > 1 && {
            frequencyCaps: {
              ...currentDraft.frequencyCaps,
              daily: null,
              weekly: null,
            },
          }),
        };
        break;
      case 'bubbleId':
        draftChange = {
          ...currentDraft,
          bubbleId: value.bubbleId,
        };
        break;
      case 'priority':
        draftChange = {
          ...currentDraft,
          priority: value,
        };
        break;
      case 'type':
        let cityIds: number[];
        cityIds = value == GlanceType.SPONSORED ? currentDraft.cityIds : [];
        draftChange = {
          ...currentDraft,
          type: value,
          cityIds: cityIds,
        };
        break;
      case 'isDspCampaign':
        draftChange = {
          ...currentDraft,
          dspCampaignData: {
            isDspCampaign: value,
            dspAdvertiserGuid: this.getDSPAdvertiserGuid(
              currentDraft.peek.sourceName,
            ),
          },
          preferedRegions:
            value && currentDraft.preferedRegions
              ? [
                  ...currentDraft.preferedRegions.filter(
                    (e) => e == Region.IN || e == Region.ID,
                  ),
                ]
              : currentDraft.preferedRegions,
          categoryIds: value ? ['#daily_digest'] : currentDraft.categoryIds,
          peek: {
            ...currentDraft.peek,
            glanceContext: value
              ? GlanceContextType.FEATURED
              : currentDraft.peek.glanceContext,
          },
        };
        break;
      case 'title':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            title: value,
          },
        };
        break;
      case 'shortTitle':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            shortTitle: value,
          },
        };
        break;
      case 'source':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            sourceName: value,
          },
          ...(currentDraft.dspCampaignData &&
          currentDraft.dspCampaignData.isDspCampaign
            ? {
                dspCampaignData: {
                  ...currentDraft.dspCampaignData,
                  dspAdvertiserGuid: this.getDSPAdvertiserGuid(value),
                },
              }
            : {}),
        };
        break;
      case 'hearts':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            heartCount: value ? parseInt(value) : undefined,
          },
        };
        break;
      case 'ctaUrl':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            ctaUrl: value == null ? null : value.trim(),
          },
        };
        break;
      case 'videoUrl':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            videoUrl: value == null ? null : value.trim(),
          },
        };
        break;
      case 'webPeekUrl':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            webPeekUrl: value == null ? null : value.trim(),
          },
        };
        break;
      case 'shouldUnlock':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            shouldUnlock: value,
          },
        };
        break;
      case 'openInBg':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            cta: {
              ...currentDraft.peek.cta,
              openUrlCta: {
                ...currentDraft.peek.cta.openUrlCta,
                openInBg: value,
              },
            },
          },
        };
        break;
      case 'shouldUnlockAppInstalled':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            appCta: {
              ...currentDraft.peek.appCta,
              appInstalledCta: {
                ...currentDraft.peek.appCta.appInstalledCta,
                shouldUnlock: value,
              },
            },
          },
        };
        break;

      case 'ociAppSelection':
        const appItem: any = value;
        const appMeta: AppInstallMeta = {
          secondaryBrandColor: appItem.secondaryBrandColor,
          logoUrl: appItem.logoUrl,
          appName: appItem.name,
          category: appItem.category,
          size: appItem.size,
          rating: appItem.rating,
          downloadCount: appItem.downloadCount,
          title: appItem.title,
          description: appItem.description,
          screenshotUrls: appItem.screenshotUrls,
          packageName: appItem.packageId,
          appBundleUrl: '',
          notifTemplateId: '',
          d1NotifTemplateId: '',
          ltvNotifTemplateId: '',
          appId: appItem.appId,
          brandColor: appItem.brandColor,
          brandBgImage: appItem.brandBgImage,
          appOptionalDetails: appItem.appOptionalDetails,
          preloadEnable: appItem.preloadEnable ? appItem.preloadEnable : false,
          contentProviderUrl: appItem.contentProviderUrl
            ? appItem.contentProviderUrl
            : '',
          prldAttr: appItem.prldAttr,
          templateId: appItem.templateId,
          tag: appItem.tag,
          reviewCount: appItem.reviewCount,
          sizeInMb: appItem.sizeInMb,
          categoryInfo: appItem.categoryInfo,
          bgColor: appItem.bgColor,
          textColor: appItem.textColor,
          carouselImages: appItem.carouselImages,
          installNowCta: appItem.installNowCta,
          installLaterCta: appItem.installLaterCta,
          fbu: appItem.fbu,
          sourceName: appItem.sourceName,
          dspAdvertiserGuid: appItem.dspAdvertiserGuid,
        };
        let defaultBeacons = {};
        if (appItem.clickBeaconUrl) {
          const beacon: Beacon = {
            url: appItem.clickBeaconUrl,
            minDurationInSecs: 0,
            delayDurationInMillis: 0,
            beaconPartner: '',
          };
          const beaconList: Array<Beacon> = [beacon];
          defaultBeacons = {
            appSubmitBeacons: beaconList,
            peekBeacons: beaconList,
            ctaBeacons: beaconList,
          };
        }
        let peekUrl = '';
        if (currentDraft.peek) {
          if (currentDraft.peek.videoUrl) {
            peekUrl = currentDraft.peek.videoCtaUrl || '';
          } else if (currentDraft.peek.ctaUrl) {
            peekUrl = currentDraft.peek.ctaUrl || '';
          }
        }
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            appCta: {
              ...currentDraft.peek.appCta,
              appInstallMeta: appMeta,
              appInstallCta: {
                ...this.state.draft.peek.appCta.appInstallCta,
                ctaDisplay: {
                  ...this.state.draft.peek.appCta.appInstallCta.ctaDisplay,
                  ctaDisplayText: {
                    ...this.state.draft.peek.appCta.appInstallCta.ctaDisplay
                      .ctaDisplayText,
                    text: currentDraft.peek.lockscreenCtaText || 'Install',
                  },
                  ctaDisplayType: CtaDisplayType.TEXT,
                },
                shouldUnlock: true,
              },

              appInstallingCta: {
                ...currentDraft.peek.appCta.appInstallingCta,
                ctaDisplay: {
                  ...currentDraft.peek.appCta.appInstallingCta.ctaDisplay,
                  ctaDisplayText: {
                    ...currentDraft.peek.appCta.appInstallingCta.ctaDisplay
                      .ctaDisplayText,
                    text: 'Installing',
                  },
                  ctaDisplayType: CtaDisplayType.TEXT,
                },
                shouldUnlock: true,
              },

              appInstalledCta: {
                ...currentDraft.peek.appCta.appInstalledCta,
                ctaDisplay: {
                  ...currentDraft.peek.appCta.appInstalledCta.ctaDisplay,
                  ctaDisplayText: {
                    ...currentDraft.peek.appCta.appInstalledCta.ctaDisplay
                      .ctaDisplayText,
                    text: 'Open',
                  },
                  ctaDisplayType: CtaDisplayType.TEXT,
                },
                shouldUnlock: true,
              },
            },
            ctaType:
              peekUrl.includes('.zip') && currentDraft.type == 4
                ? PropositionType.CTA_URL
                : PropositionType.APP_INSTALL,
          },

          beacons: {
            ...currentDraft.beacons,
            ...defaultBeacons,
          },
        };
        break;

      case 'notifTemplateId':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            appCta: {
              ...currentDraft.peek.appCta,
              appInstallMeta: {
                ...currentDraft.peek.appCta.appInstallMeta,
                notifTemplateId: value,
              },
            },
          },
        };
        break;

      case 'ltvNotifTemplateId':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            appCta: {
              ...currentDraft.peek.appCta,
              appInstallMeta: {
                ...currentDraft.peek.appCta.appInstallMeta,
                ltvNotifTemplateId: value,
              },
            },
          },
        };
        break;

      case 'd1NotifTemplateId':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            appCta: {
              ...currentDraft.peek.appCta,
              appInstallMeta: {
                ...currentDraft.peek.appCta.appInstallMeta,
                d1NotifTemplateId: value,
              },
            },
          },
        };
        break;

      case 'summary':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            summary: value,
          },
        };
        break;
      case 'cheesySummary':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            cheesySummary: value,
          },
        };
        break;
      case 'tags':
        draftChange = {
          ...currentDraft,
          tags: [value],
        };
        break;
      case 'preferedRegions':
        draftChange = {
          ...currentDraft,
          preferedRegions: value,
          priority:
            currentDraft.priority === 2 && !currentDraft.peek.webPeekUrl
              ? 0
              : currentDraft.priority,
        };
        break;
      case 'attribution_caption':
        if (currentDraft.image) {
          draftChange = {
            ...currentDraft,
            image: {
              ...currentDraft.image,
              attribution: {
                ...currentDraft.image.attribution,
                caption: value,
              },
            },
          };
        } else {
          this.setState({
            ...this.state,
            dropImage: {
              ...this.state.dropImage,
              attrCaption: value,
            },
            openSnackbar: false,
          });
          return;
        }
        break;
      case 'attribution_source':
        if (currentDraft.image) {
          draftChange = {
            ...currentDraft,
            image: {
              ...currentDraft.image,
              attribution: {
                ...currentDraft.image.attribution,
                source: value,
              },
            },
          };
        } else {
          this.setState({
            ...this.state,
            dropImage: {
              ...this.state.dropImage,
              attrSource: value,
            },
            openSnackbar: false,
          });
          return;
        }
        break;
      case 'curationReason':
        draftChange = {
          ...currentDraft,
          editMeta: {
            ...currentDraft.editMeta,
            curationReason: value,
          },
        };
        break;
      case 'publisherData':
        draftChange = {
          ...currentDraft,
          publisherData: value,
        };
        break;
      case 'categoryIds':
        draftChange = {
          ...currentDraft,
          categoryIds: value,
        };
        break;
      case 'sourceCreatorId':
        draftChange = {
          ...currentDraft,
          sourceCreatorId: value,
        };
        break;
      case 'cityIds':
        draftChange = {
          ...currentDraft,
          cityIds: value,
        };
        break;
      case 'ociConfigs':
        draftChange = {
          ...currentDraft,
          ociConfigs: value,
        };
        break;
      case 'carriers':
        draftChange = {
          ...currentDraft,
          carriers: value,
        };
        break;
      case 'targetingNegative':
        draftChange = {
          ...currentDraft,
          targeting: {
            ...draftTargeting,
            apps: {
              ...draftTargeting.apps,
              //update targeting view and fields are mutually exclusive
              [value[1]]: value[0],
              [value[2][0]]: [],
            },
          },
        };
        break;
      case 'targetingPositive':
        draftChange = {
          ...currentDraft,
          targeting: {
            ...draftTargeting,
            apps: {
              ...draftTargeting.apps,
              //update targeting view and fields are mutually exclusive
              [value[1]]: value[0],
              [value[2][0]]: [],
            },
          },
        };
        break;
      case 'gPayReadiness':
        draftChange = {
          ...currentDraft,
          targeting: {
            ...draftTargeting,
            requiresGPayReadiness: value,
          },
        };
        break;
      case 'dspRetargeting':
        draftChange = {
          ...currentDraft,
          targeting: {
            ...draftTargeting,
            enableDSPRetargeting: value,
          },
        };
        break;
      case 'childlockflag':
        draftChange = {
          ...currentDraft,
          targeting: {
            ...draftTargeting,
            childLockFlag: value,
          },
        };
        break;
      case 'targetingNetwork':
        draftChange = {
          ...currentDraft,
          userNetworkTypes: value,
        };
        break;
      case 'useUnlockNudge':
        draftChange = {
          ...currentDraft,
          useUnlockNudge: value,
        };
        break;
      case 'segmentIds':
        draftChange = {
          ...currentDraft,
          segmentIds: value,
        };
        break;

      case 'antiSegmentIds':
        draftChange = {
          ...currentDraft,
          antiSegmentIds: value,
        };
        break;
      case 'cta_text':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            cta: {
              ...currentDraft.peek.cta,
              text: value,
            },
          },
        };
        break;
      case 'storyType':
        draftChange = {
          ...currentDraft,
          storyType: value,
        };
        break;
      case 'languageLimit':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            summaryLimit: value,
          },
        };
        break;
      case 'targetForCategories':
        draftChange = {
          ...currentDraft,
          targetForCategories: value,
        };
        break;
      case 'categorySubscriptionStatus':
        draftChange = {
          ...currentDraft,
          categorySubscriptionStatus: value,
        };
        break;
      case 'canSkipSummary':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            canSkipSummary: value,
          },
        };
        break;
      case 'dailyCap':
        draftChange = {
          ...currentDraft,
          frequencyCaps: {
            ...currentDraft.frequencyCaps,
            daily: value,
          },
        };
        break;
      case 'weeklyCap':
        draftChange = {
          ...currentDraft,
          frequencyCaps: {
            ...currentDraft.frequencyCaps,
            weekly: value,
          },
        };
        break;
      case 'stickiness':
        draftChange = {
          ...currentDraft,
          stickinessCount: value,
        };
        break;
      case 'renderSessionProperties':
        draftChange = {
          ...currentDraft,
          renderSessionProperties: value,
          renderSessionMode: value ? currentDraft.renderSessionMode : null,
        };
        break;
      case 'startDay':
      case 'endDay':
        draftChange = {
          ...currentDraft,
          userLifeTimeFilter: {
            ...currentDraft.userLifeTimeFilter,
            [key]: value,
          },
        };
        break;
      case 'OfflinePeekLink':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            offlinePeekUrl:
              value === null ? null : value.trim() !== '' ? value.trim() : null,
          },
        };
        break;
      case 'VideoCtaUrl':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            videoCtaUrl: value == null ? null : value.trim(),
            cta: {
              ...currentDraft.peek.cta,
              url: value == null ? null : value.trim(),
              originalUrl: value == null ? null : value.trim(),
            },
          },
        };
        break;
      case 'ArticleCtaUrl':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            articleCtaUrl: value == null ? null : value.trim(),
            cta: {
              ...currentDraft.peek.cta,
              url: value == null ? null : value.trim(),
              originalUrl: value == null ? null : value.trim(),
            },
          },
        };
        break;
      case 'subText':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            subText: value,
          },
        };
        break;
      case 'useBubbleImageAsRibbonIcon':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            useBubbleImageAsRibbonIcon: value,
          },
        };
        break;
      case 'ctaType':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            ctaType: value,
          },
        };
        break;
      case 'autoOpenCtaDurationInSecs':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            cta: {
              url: '',
              ...currentDraft.peek.cta,
              autoOpenCtaDurationInSecs: value,
            },
          },
        };
        break;
      case 'InstallLink':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            ctaUrl: value,
            appCta: {
              ...currentDraft.peek.appCta,
              appInstallCta: {
                ...currentDraft.peek.appCta.appInstallCta,
                url: value,
              },
            },
          },
        };
        break;
      case 'InstalledLink':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            appCta: {
              ...currentDraft.peek.appCta,
              appInstalledCta: {
                ...currentDraft.peek.appCta.appInstalledCta,
                url: value,
              },
            },
          },
        };
        break;
      case 'InstallingLink':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            appCta: {
              ...currentDraft.peek.appCta,
              appInstallingCta: {
                ...currentDraft.peek.appCta.appInstallingCta,
                url: value,
              },
            },
          },
        };
        break;
      // case 'InstallCtaText':
      //     draftChange = {
      //         ...currentDraft,
      //         peek: {
      //             ...currentDraft.peek,
      //             appCta: {
      //                 ...currentDraft.peek.appCta,
      //                 appInstallCta: {
      //                     ...currentDraft.peek.appCta.appInstallCta,
      //                     ctaDisplay: {
      //                         ...currentDraft.peek.appCta.appInstallCta.ctaDisplay,
      //                         ctaDisplayText: {
      //                             ...currentDraft.peek.appCta.appInstallCta.ctaDisplay
      //                                 .ctaDisplayText,
      //                             text: value,
      //                         },
      //                         ctaDisplayType: CtaDisplayType.TEXT,
      //                     },
      //                 },
      //             },
      //         },
      //     };
      //     break;
      case 'InstalledCtaText':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            appCta: {
              ...currentDraft.peek.appCta,
              appInstalledCta: {
                ...currentDraft.peek.appCta.appInstalledCta,
                ctaDisplay: {
                  ...currentDraft.peek.appCta.appInstalledCta.ctaDisplay,
                  ctaDisplayText: {
                    ...currentDraft.peek.appCta.appInstalledCta.ctaDisplay
                      .ctaDisplayText,
                    text: value,
                  },
                  ctaDisplayType: CtaDisplayType.TEXT,
                },
              },
            },
          },
        };
        break;
      case 'InstallingCtaText':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            appCta: {
              ...currentDraft.peek.appCta,
              appInstallingCta: {
                ...currentDraft.peek.appCta.appInstallingCta,
                ctaDisplay: {
                  ...currentDraft.peek.appCta.appInstallingCta.ctaDisplay,
                  ctaDisplayText: {
                    ...currentDraft.peek.appCta.appInstallingCta.ctaDisplay
                      .ctaDisplayText,
                    text: value,
                  },
                  ctaDisplayType: CtaDisplayType.TEXT,
                },
              },
            },
          },
        };
        break;
      case 'AppPackageName':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            appCta: {
              ...currentDraft.peek.appCta,
              appInstallMeta: {
                ...currentDraft.peek.appCta.appInstallMeta,
                packageName: value,
              },
            },
          },
        };
        break;
      case 'AppName':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            appCta: {
              ...currentDraft.peek.appCta,
              appInstallMeta: {
                ...currentDraft.peek.appCta.appInstallMeta,
                appName: value,
              },
            },
          },
        };
        break;
      case 'isShareable':
        draftChange = {
          ...currentDraft,
          isShareable: value,
        };
        break;
      case 'nativeVideoOverflow':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            nativeVideoOverflow: value,
          },
        };
        break;

      case 'shareUrl':
        draftChange = {
          ...currentDraft,
          shareUrl: value,
        };
        break;

      case 'hardwareAccelerated':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            hardwareAccelerated: value,
          },
        };
        break;
      case 'loadAndroidJs':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            loadAndroidJs: value,
          },
        };
        break;
      case 'hardwareAccelerated':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            hardwareAccelerated: value,
          },
        };
        break;
      case 'loadAndroidJs':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            loadAndroidJs: value,
          },
        };
        break;
      case 'lockscreen_cta_text':
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            lockscreenCtaText: value,
            ...(currentDraft.peek.appCta.appInstallCta.ctaDisplay.ctaDisplayText
              ? {
                  appCta: {
                    ...currentDraft.peek.appCta,
                    appInstallCta: {
                      ...this.state.draft.peek.appCta.appInstallCta,
                      ctaDisplay: {
                        ...this.state.draft.peek.appCta.appInstallCta
                          .ctaDisplay,
                        ctaDisplayText: {
                          ...this.state.draft.peek.appCta.appInstallCta
                            .ctaDisplay.ctaDisplayText,
                          text: value || 'Install',
                        },
                        ctaDisplayType: CtaDisplayType.TEXT,
                      },
                      shouldUnlock: true,
                    },
                  },
                }
              : {}),
          },
        };
        break;

      case 'offlineShoppingZipUrl':
        draftChange = {
          ...currentDraft,
          shoppableData: {
            ...currentDraft.shoppableData,
            offlineShoppingZipUrl: value,
          },
        };
        break;

      case 'iconImageUrl':
        draftChange = {
          ...currentDraft,
          shoppableData: {
            ...currentDraft.shoppableData,
            iconImageUrl: value,
          },
        };
        break;

      case 'shoppingUrl':
        draftChange = {
          ...currentDraft,
          shoppableData: {
            ...currentDraft.shoppableData,
            shoppingUrl: value,
          },
        };
        break;

      case 'isShoppable':
        draftChange = {
          ...currentDraft,
          isShoppable: value,
        };
        break;
      case BeaconType[BeaconType.CTA]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            ctaBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.VIDEO]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            videoBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.RENDER]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            renderBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.PEEK]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            peekBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.LIKE]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            likeBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.SHARE]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            shareBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.VIDEO_STARTED]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            videoStartedBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.HOLD]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            holdBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.APP_SUBMIT]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            appSubmitBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.INSTALL_NOW]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            installNowBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.INSTALL_LATER]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            installLaterBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.INSTALL_COMPLETE]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            installCompleteBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.APP_OPEN]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            appOpenBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.GLANCE_STARTED]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            glanceStartedBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.PRIVIEW_TAP]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            previewTapBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.VIDEO_VIEWED]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            videoViewedBeacons: value,
          },
        };
        break;
      case BeaconType[BeaconType.GLANCE_VIEWED]:
        draftChange = {
          ...currentDraft,
          beacons: {
            ...currentDraft.beacons,
            glanceViewedBeacons: value,
          },
        };
        break;
      case 's2sIntegration':
        draftChange = {
          ...currentDraft,
          beaconsInfo: {
            ...currentDraft.beaconsInfo,
            s2sIntegration: value,
          },
        };
        break;
      case 'advertiserId':
        draftChange = {
          ...currentDraft,
          programmaticDetail: {
            ...currentDraft.programmaticDetail,
            advertiserId: value,
          },
        };
        break;
      case 'cappingPacingCheck':
        draftChange = {
          ...currentDraft,
          programmaticDetail: {
            ...currentDraft.programmaticDetail,
            cappingPacingCheck: value,
          },
        };
        break;
      case 'creativeId':
        draftChange = {
          ...currentDraft,
          programmaticDetail: {
            ...currentDraft.programmaticDetail,
            creativeId: value,
          },
        };
        break;
      case 'channelType':
        draftChange = {
          ...currentDraft,
          programmaticDetail: {
            ...currentDraft.programmaticDetail,
            channelType: value,
          },
        };
        break;
      case 'language':
        draftChange = {
          ...currentDraft,
          glanceLanguageId: value,
        };
        break;
      case 'topicName':
        draftChange = {
          ...currentDraft,
          topicName: value,
        };
        break;
      case 'homescreenWorthy':
        draftChange = {
          ...currentDraft,
          homescreenWorthy: value,
        };
        break;
      case 'dynamicOverlay':
        draftChange = {
          ...currentDraft,
          dynamicOverlay: value,
        };
        break;
      case 'allowKeyboard':
        draftChange = {
          ...currentDraft,
          allowKeyboard: value,
        };
        break;
      case 'allowNativeKeyboard':
        draftChange = {
          ...currentDraft,
          allowNativeKeyboard: value,
        };
        break;
      case 'campaignObjective':
        draftChange = {
          ...currentDraft,
          campaignMeta: {
            ...currentDraft.campaignMeta,
            objective: value,
          },
        };
        break;
      case 'campaignEcpm':
        draftChange = {
          ...currentDraft,
          campaignMeta: {
            ...currentDraft.campaignMeta,
            eCpm: value,
          },
        };
        break;
      case 'glroId':
        draftChange = {
          ...currentDraft,
          campaignMeta: {
            ...currentDraft.campaignMeta,
            glroId: value,
          },
        };
        break;
      case 'impressionCappingData':
        draftChange = {
          ...currentDraft,
          impressionCappingData: value,
        };
        break;
      case 'totalImpressionRequired':
        draftChange = {
          ...currentDraft,
          impressionCappingData: {
            ...currentDraft.impressionCappingData,
            totalImpressionRequired: value,
          },
        };
        break;
      case 'triggerPercentage':
        draftChange = {
          ...currentDraft,
          impressionCappingData: {
            ...currentDraft.impressionCappingData,
            triggerPercentage: value,
          },
        };
        break;
      case 'servingCappingEnabled':
        draftChange = {
          ...currentDraft,
          impressionCappingData: {
            ...currentDraft.impressionCappingData,
            servingCappingEnabled: value,
          },
        };
        break;
      case 'glanceTimer':
        draftChange = {
          ...currentDraft,
          glanceBubbleDetails: {
            ...currentDraft.glanceBubbleDetails,
            glanceTimer: value * 1000,
          },
        };
        break;
      // OMSDK cases start here
      case 'omSdkInfoType':
        draftChange = {
          ...currentDraft,
          omSdkInfo: {
            ...currentDraft.omSdkInfo,
            type: value,
          },
        };
        break;
      case 'validationUrl':
        draftChange = {
          ...currentDraft,
          omSdkInfo: {
            ...currentDraft.omSdkInfo,
            validationUrl: value,
          },
        };
        break;
      case 'verificationParam':
        draftChange = {
          ...currentDraft,
          omSdkInfo: {
            ...currentDraft.omSdkInfo,
            verificationParam: value,
          },
        };
        break;
      case 'vendorKey':
        draftChange = {
          ...currentDraft,
          omSdkInfo: {
            ...currentDraft.omSdkInfo,
            vendorKey: value,
          },
        };
        break;
      case 'ctaview_height':
        draftChange = {
          ...currentDraft,
          ctaViewConfig: {
            ...currentDraft.ctaViewConfig,
            height: value,
          },
        };
        break;
      case 'ctaview_hide_toppadding':
        draftChange = {
          ...currentDraft,
          ctaViewConfig: {
            ...currentDraft.ctaViewConfig,
            removeTopPadding: value,
          },
        };
        break;
      case 'ctaview_hide_crossicon':
        draftChange = {
          ...currentDraft,
          ctaViewConfig: {
            ...currentDraft.ctaViewConfig,
            hideCrossIcon: value,
          },
        };
        break;
      //omsdk cases ends
      case 'paused':
        draftChange = {
          ...currentDraft,
          paused: value,
        };
        break;
      case 'adCoefficient':
        draftChange = {
          ...currentDraft,
          adCoefficient: value,
        };
        break;
      case 'bucketPriority':
        draftChange = {
          ...currentDraft,
          bucketPriority: value,
        };
        break;
      case 'gamPgAdUnitId':
        draftChange = {
          ...currentDraft,
          gamPgAdUnitId: value,
        };
        break;
      case 'ecpm':
        draftChange = {
          ...currentDraft,
          campaignMeta: {
            ...currentDraft.campaignMeta,
            eCpm: value,
          },
        };
        break;
      case 'sponsoredBubbleChange':
        console.log('case sponsoredBubbleChange', value);
        draftChange = {
          ...currentDraft,
          bubbleId: value.bubbleId,
          adCoefficient: value.adCoefficient,
          bucketPriority: value.bucketPriority,
          rankInBubble: value.rankInBubble,
          campaignMeta: {
            ...currentDraft.campaignMeta,
            eCpm: value.eCpm,
          },
        };
        adCoeffDisabled = value.adCoefficientDisabled;
        bucketPriorityDisabled = value.bucketPriorityDisabled;
        eCpmDisabled = value.eCpmDisabled;
        storyCount = value.storyCount;
        displayRank = value.rankInBubbleDispay;
        storyCount = value.storyCount;
        // rankDisplay = value.rankInBubbleDispay
        break;
      case 'isBeautifulWallpaperEnabled':
        draftChange = {
          ...currentDraft,
          isBeautifulWallpaperEnabled: value,
        };
        break;
      case 'eu':
        const { updatedEus, valueProps } = getUpdatedEus(value);
        draftChange = {
          ...currentDraft,
          eus: updatedEus.length > 0 ? updatedEus : null,
          valueProps: valueProps.length > 0 ? valueProps : null,
        };
        break;
      case 'valueProp':
        const { updatedValueProps } = getUpdatedValueProps(value);
        draftChange = {
          ...currentDraft,
          valueProp: value.vpId,
          valueProps: updatedValueProps.length > 0 ? updatedValueProps : null,
        };
        break;
      default:
        console.error('What the hell!', key, value);
    }
    // Send appInstallMeta for Google Playstore Campaign's
    if (
      (key === 'source' || key === 'campaignObjective') &&
      draftChange.campaignMeta &&
      draftChange.campaignMeta.objective &&
      draftChange.campaignMeta.objective === 'V1_Google Playstore'
    ) {
      // Only for Sponsored
      if (draftChange.type === 4) {
        draftChange = {
          ...draftChange,
          peek: {
            ...draftChange.peek,
            appCta: {
              ...draftChange.peek.appCta,
              appInstallMeta:
                this.getAppInstallMeta(
                  key === 'source' ? value : draftChange.peek.sourceName,
                ) || draftChange.peek.appCta.appInstallMeta,
            },
          },
        };
      }
    }
    if (key === 'bubbleId') {
      this.setState({
        ...this.state,
        pristine: false,
        draft: new GlanceDraft(draftChange),
        bubbleData: value,
      });
    } else if (key === 'glanceContextType') {
      if (value === GlanceContextType.LIVE) {
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            useBubbleImageAsRibbonIcon: false,
            glanceContext: value,
          },
        };
      } else {
        draftChange = {
          ...currentDraft,
          peek: {
            ...currentDraft.peek,
            useBubbleImageAsRibbonIcon: false,
            glanceContext: value,
          },
        };
      }
      this.setState({
        ...this.state,
        pristine: false,
        draft: new GlanceDraft(draftChange),
        editAdCoefficient: adCoeffDisabled,
        editBucketPriority: bucketPriorityDisabled,
        editEcpm: eCpmDisabled,
        displayRank: displayRank,
        storyCount: storyCount,
      });
    } else {
      this.setState({
        ...this.state,
        pristine: false,
        draft: new GlanceDraft(draftChange),
        editAdCoefficient: adCoeffDisabled,
        editBucketPriority: bucketPriorityDisabled,
        editEcpm: eCpmDisabled,
        displayRank: displayRank,
        storyCount: storyCount,
      });
    }
  }

  async onSave(isCTASecReq) {
    let draftChange = this.state.draft;

    if (
      this.state.draft.type === GlanceType.SPONSORED &&
      this.state.bubbleData &&
      this.state.bubbleData.storyCount > 0
    ) {
      const { rankInBubble } = this.state.draft;
      if (rankInBubble === null) {
        this.setState({
          ...this.state,
          openSnackbar: true,
          snackbarMessage: 'Missing Rank',
        });
        return;
      }
    } else {
      draftChange = {
        ...draftChange,
        rankInBubble: null,
      };
    }

    if (
      this.state.draft.type === GlanceType.SPONSORED &&
      (this.state.draft.campaignMeta === null ||
        this.state.draft.campaignMeta.objective === null)
    ) {
      this.setState({
        ...this.state,
        openSnackbar: true,
        snackbarMessage: 'Campaign Objective Missing',
      });
      return;
    }

    if (
      this.state.draft.type !== GlanceType.SPONSORED &&
      this.state.draft.preferedRegions.length
    ) {
      const selectValuePropsMessage =
        'Please select EU and Value Prop for India and/or Indonesia';
      let isValuePropSelected = true;
      const mandatoryValuePropRegions = [Region.IN, Region.ID];
      this.state.draft.preferedRegions.forEach((region: Region) => {
        if (mandatoryValuePropRegions.includes(region)) {
          if (this.state.draft.valueProps === null) {
            this.setState({
              ...this.state,
              openSnackbar: true,
              snackbarMessage: selectValuePropsMessage,
            });
            isValuePropSelected = false;
          }
          if (
            !this.state.draft.valueProps
              .map((valueProp) => valueProp.region)
              .includes(region)
          ) {
            this.setState({
              ...this.state,
              openSnackbar: true,
              snackbarMessage: selectValuePropsMessage,
            });
            isValuePropSelected = false;
          }
        }
      });
      if (!isValuePropSelected) return;
    }

    this.setState({
      ...this.state,
      openSnackbar: true,
      snackbarMessage: 'Uploading Image...',
      draft: new GlanceDraft(draftChange),
    });
    if (this.state.dropImage && this.state.dropImage.image) {
      if (!this.state.dropImage.attrSource) {
        this.setState({
          ...this.state,
          openSnackbar: true,
          snackbarMessage: 'Missing attribution details',
        });
        return;
      }
      await RestService.uploadImage(this.state.dropImage.image, (image) => {
        if (image != null) {
          this.setState({
            ...this.state,
            draft: new GlanceDraft({
              ...this.state.draft,
              imageId: image.id,
            }),
          });
        }
      });
    }

    if (isCTASecReq) {
      let errorMessage = '';
      if (
        !this.state.draft.peek.cta.autoOpenCtaDurationInSecs &&
        this.state.draft.peek.cta.autoOpenCtaDurationInSecs !== 0
      ) {
        errorMessage = 'Missing CTA duration';
      } else if (
        this.state.draft.peek.cta.autoOpenCtaDurationInSecs &&
        this.state.draft.peek.cta.autoOpenCtaDurationInSecs < 0
      ) {
        errorMessage = 'CTA duration should not be negative value';
      }
      if (errorMessage) {
        this.setState({
          ...this.state,
          openSnackbar: true,
          snackbarMessage: errorMessage,
        });
        return;
      }
    }

    if (
      this.state.overlayImage &&
      this.state.overlayImage.image &&
      this.state.overlayImage.image.name.endsWith('.png')
    ) {
      await RestService.uploadImage(
        this.state.overlayImage.image,
        (overlayImage) => {
          if (overlayImage != null) {
            this.setState({
              ...this.state,
              draft: new GlanceDraft({
                ...this.state.draft,
                overlayImageId: overlayImage.id,
              }),
            });
          }
        },
      );
    }

    if (this.state.bingeImage && this.state.bingeImage.image) {
      await RestService.uploadImage(
        this.state.bingeImage.image,
        (bingeImage) => {
          if (bingeImage != null) {
            this.setState({
              ...this.state,
              draft: new GlanceDraft({
                ...this.state.draft,
                bingeImageId: bingeImage.id,
              }),
            });
          }
        },
      );
    }
    this.saveDraft();
  }

  saveDraft() {
    const that = this;
    console.log('draft', this.state.draft);
    this.setState({
      ...this.state,
      openSnackbar: true,
      snackbarMessage: 'Image Uploaded. Saving Draft...',
    });
    RestService.saveDraft(this.state.draft, function(newDraft: GlanceDraft) {
      if (newDraft) {
        let updatedDraft: GlanceDraft;
        let updateDraftFlag = false;
        if (that.state.bingeImage && that.state.bingeImage.image) {
          updatedDraft = {
            ...newDraft,
            bingeImage: {
              ...newDraft.bingeImage,
            },
          };
          updateDraftFlag = true;
        }
        if (
          that.state.overlayImage &&
          that.state.overlayImage.image &&
          that.state.overlayImage.image.name.endsWith('.png')
        ) {
          updatedDraft = {
            ...newDraft,
            overlayImage: {
              ...newDraft.overlayImage,
            },
          };
          updateDraftFlag = true;
        }
        if (that.state.dropImage && that.state.dropImage.image) {
          updatedDraft = {
            ...newDraft,
            image: {
              ...newDraft.image,
              attribution: {
                ...newDraft.image.attribution,
                source: that.state.dropImage.attrSource,
                author: that.state.dropImage.attrAuthor,
              },
            },
          };
          updateDraftFlag = true;
        }
        if (updateDraftFlag) {
          RestService.saveDraft(new GlanceDraft(updatedDraft), function(
            updatedNewDraft: GlanceDraft,
          ) {
            that.setState({
              ...that.state,
              pristine: true,
              openSnackbar: true,
              dropImage: null,
              snackbarMessage: 'Draft Saved.',
              draft: updatedNewDraft,
            });
          });
        } else {
          that.setState({
            ...that.state,
            pristine: true,
            openSnackbar: true,
            dropImage: null,
            snackbarMessage: 'Draft Saved.',
            draft: newDraft,
          });
        }
      } else {
        that.setState({
          ...that.state,
          openSnackbar: true,
          snackbarMessage: 'Draft Save failed.',
        });
      }
    });
  }

  onOverride() {
    console.log('onOverride');
    this.setState({
      ...this.state,
      openSyncConfirmDialog: true,
    });
  }

  onOverrideConfirm() {
    console.log('onOverrideConfirm');
    let that = this;
    that.setState({
      ...that.state,
      openSnackbar: true,
      snackbarMessage: 'Syncing publish data ...',
      openSyncConfirmDialog: false,
    });
    RestService.overrideDraft(this.state.draft, function(error: any) {
      if (error == null) {
        that.setState({
          ...that.state,
          openSnackbar: true,
          snackbarMessage: 'Publish data synced',
          openSyncConfirmDialog: false,
        });
      } else {
        that.setState({
          ...that.state,
          openSnackbar: true,
          snackbarMessage:
            "Publish data sync failed. Most common reason is that draft isn't published",
          openSyncConfirmDialog: false,
        });
      }
    });
  }

  onRemove() {
    console.log('onRemove');
    this.setState({
      ...this.state,
      openRemoveConfirmDialog: true,
    });
  }

  onPause() {
    const { paused, draftId } = this.state.draft;
    RestService.glancePauseRequest([draftId], !paused, (success) => {});
    this.setState({
      ...this.state,
      openSnackbar: true,
      snackbarMessage: 'Campaign Paused',
    });
  }
  removeImage() {
    this.setState({
      ...this.state,
      draft: new GlanceDraft({
        ...this.state.draft,
        image: undefined,
        imageId: undefined,
      }),
      dropImage: {
        ...this.state.dropImage,
        image: undefined,
      },
      pristine: false,
    });
  }

  removeOverlayImage() {
    this.setState({
      ...this.state,
      overlayImage: {
        ...this.state.overlayImage,
        image: undefined,
      },
      draft: new GlanceDraft({
        ...this.state.draft,
        overlayImage: undefined,
        overlayImageId: undefined,
      }),
      pristine: false,
    });
  }

  removeBingeImage() {
    this.setState({
      ...this.state,
      bingeImage: {
        ...this.state.bingeImage,
        image: undefined,
      },
      draft: new GlanceDraft({
        ...this.state.draft,
        bingeImage: undefined,
        bingeImageId: undefined,
      }),
      pristine: false,
    });
  }

  onImageDrop(image: any) {
    this.setState({
      ...this.state,
      pristine: false,
      dropImage: {
        ...this.state.dropImage,
        image: image,
      },
    });
  }

  onOverlayImageDrop(image: any) {
    this.setState({
      ...this.state,
      pristine: false,
      overlayImage: {
        ...this.state.overlayImage,
        image: image,
      },
    });
  }

  onBingeImageDrop(image: any) {
    this.setState({
      ...this.state,
      pristine: false,
      bingeImage: {
        ...this.state.bingeImage,
        image: image,
      },
    });
  }

  onPublish() {
    console.log('onPublish');
    this.setState({
      ...this.state,
      onPublishClicked: true,
      openPublishDialog: true,
    });
  }

  onPublishDone(newDraft: GlanceDraft) {
    console.log('onPublishConfirm');
  }

  onRemoveConfirm() {
    RestService.removeDraft(this.state.draft.draftId);
    this.setState({
      ...this.state,
      removed: true,
      openRemoveConfirmDialog: false,
    });
  }

  closeConfirmDialog() {
    this.setState({
      ...this.state,
      openRemoveConfirmDialog: false,
      openSyncConfirmDialog: false,
    });
  }
  closePublishDialog() {
    this.setState({
      ...this.state,
      openPublishDialog: false,
    });
  }
  closeSnackbar() {
    this.setState({
      ...this.state,
      openSnackbar: false,
    });
  }

  render() {
    const draft = this.state.draft;
    const userRole = this.props.studioAppConfig.currentToken.role;

    const actions = [];
    actions.push(
      <FlatButton
        label="Cancel"
        primary={true}
        onClick={this.closeConfirmDialog}
      />,
    );
    actions.push(
      <FlatButton
        label="Confirm"
        primary={true}
        onClick={() => {
          this.state.openRemoveConfirmDialog
            ? this.onRemoveConfirm()
            : this.onOverrideConfirm();
        }}
      />,
    );

    return (
      <div className="">
        <GlanceDraftCard
          cardStyle={{ paddingBottom: 'none' }}
          glanceDraftData={this.state.draft}
          draftMeta={this.props.draftMeta}
          pristine={this.state.pristine}
          removed={this.state.removed}
          dropImage={this.state.dropImage}
          onImageDrop={this.onImageDrop}
          removeImage={this.removeImage}
          onOverride={this.onOverride}
          onOverlayImageDrop={this.onOverlayImageDrop}
          onBingeImageDrop={this.onBingeImageDrop}
          removeBingeImage={this.removeBingeImage}
          removeOverlayImage={this.removeOverlayImage}
          overlayImage={this.state.overlayImage}
          bingeImage={this.state.bingeImage}
          onEdit={this.onEdit}
          onSave={this.onSave}
          onRemove={this.onRemove}
          onPublish={this.onPublish}
          onPause={this.onPause}
          studioAppConfig={this.props.studioAppConfig}
          singleClickPublish={this.props.singleClickPublish}
          errorMsg={this.props.errorMsg}
          regionPartnerList={this.props.regionPartnerList}
          role={this.props.role}
          copyDraft={this.props.copyDraft}
          disableCopy={this.props.disableCopy}
          bubbleData={this.state.bubbleData}
          cardType={this.props.cardType}
          adCoefficientDisabled={this.state.editAdCoefficient}
          bucketPriorityDisabled={this.state.editBucketPriority}
          ecpmDisabled={this.state.editEcpm}
          rankDisabled={this.state.editRank}
          fCapDisabled={this.state.editFCap}
          rankDisplay={this.state.displayRank}
          storyCount={this.state.storyCount}
          cityStates={this.props.cityStates}
          peekSourceList={this.state.peekSourceList}
          dspPeekSourceList={this.state.dspPeekSourceList}
        />
        <Dialog
          modal={false}
          open={
            this.state.openRemoveConfirmDialog ||
            this.state.openSyncConfirmDialog
          }
          onRequestClose={this.closeConfirmDialog}
          actions={actions}
        >
          {this.state.openRemoveConfirmDialog
            ? 'Please confirm that you want to remove this draft'
            : 'Please confirm that you want to sync this draft with published glances'}
        </Dialog>
        {this.state.onPublishClicked &&
        (userRole == Role.MODERATOR || userRole == Role.ADMIN) ? (
          <GlanceDraftPublish
            studioAppConf={this.props.studioAppConfig}
            draft={this.state.draft}
            open={this.state.openPublishDialog}
            experiments={this.props.experiments}
            onPublishDone={this.onPublishDone}
            closeDialog={this.closePublishDialog}
          />
        ) : (
          ''
        )}
        <Snackbar
          open={this.state.openSnackbar}
          message={this.state.snackbarMessage}
          onRequestClose={this.closeSnackbar}
        />
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    studioAppConfig: state.studioAppConfig,
    ...ownProps,
  };
};

export default connect(mapStateToProps)(GlanceDraftComponent);



// WEBPACK FOOTER //
// ./src/pages/drafts/components/GlanceDraft.tsx